import { inject, Injectable } from '@angular/core';
import { BotsService } from '@core/bots';
import { BotLanguage } from '@core/languages';
import { TranslateService } from '@ngx-translate/core';
import { ToastService, ToastType } from '@pin-up-global/common';
import {
  BehaviorSubject,
  delayWhen,
  EMPTY,
  expand,
  interval,
  map,
  Observable,
  shareReplay,
  switchMap,
  takeWhile,
  tap
} from 'rxjs';
import { GeneralSettingsApiService } from './general-settings-api.service';
import { Task, TaskState, UpdateBotInfoReq } from './general-settings.abstraction';

@Injectable()
export class GeneralSettingsService {
  #generalSettingsApiService = inject(GeneralSettingsApiService);
  #botsService = inject(BotsService);
  #translateService = inject(TranslateService);
  #toastService = inject(ToastService);

  #update$ = new BehaviorSubject<void>(void 0);
  #task$ = new BehaviorSubject<Task | null>(null);

  #botInfoRes$ = this.#update$.pipe(
    switchMap(() => this.#botsService.selectedBotId$),
    switchMap((botId) => this.#generalSettingsApiService.getBotInfo({ botId })),
    shareReplay(1),
  );

  #defaultLanguagesRes$ = this.#update$.pipe(
    switchMap(() => this.#botsService.selectedBotId$),
    switchMap((botId) => this.#generalSettingsApiService.getDefaultLanguages(botId)),
    shareReplay(1),
  );

  readonly defaultLanguages$: Observable<BotLanguage[]> = this.#defaultLanguagesRes$.pipe(map((res) => res.items));

  readonly botInfo$ = this.#botInfoRes$;

  readonly task$ = this.#task$;

  readonly isLoading$ = this.#generalSettingsApiService.isLoading$;

  refresh(): void {
    this.#update$.next();
  }

  updateBotInfo(params: Omit<UpdateBotInfoReq, 'id'>): Observable<Task> {
    return this.#botsService.selectedBotId$.pipe(
      switchMap((botId) => this.#generalSettingsApiService.updateBotInfo({ ...params, id: botId }).pipe(
        tap(() => {
          this.#toastService.show({
            type: ToastType.Success,
            title: this.#translateService.instant('generalSettingsPage.toast.settingsUpdated'),
          });
          this.#botsService.refresh();
        })),
      ),
      switchMap((bot) => bot.lockedByTask ? this.#trackBackgroundTask(bot.lockedByTask) : EMPTY)
    );
  }

  #trackBackgroundTask(taskId: string): Observable<Task> {
    return this.#generalSettingsApiService.getBackgroundTasks(taskId).pipe(
      expand((response) =>
        response.progress < 100
          ? this.#generalSettingsApiService.getBackgroundTasks(taskId).pipe(delayWhen(() => interval(1000)))
          : EMPTY
      ),
      takeWhile((response) => response.progress < 100 && response.state === TaskState.STARTED, true),
      tap((res) => this.#task$.next(res))
    );
  }
}
